import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getAllReports,
  getReportTypes,
  getSharedDrivesByIdsQuery,
  UserKeys,
} from '../../../api/users-api';
import { ReportModel, ReportType, SharedDriveModel } from '../../../../types';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { gPanelTiers } from '../../../shared/gPanel-tiers';
import { Messages } from '../../../utils/message-constants';
import { useReportActions } from '../../../shared/hooks/use-report-actions';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import AvatarChip from '../../../shared/avatar-chip';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import ModelDetailsDisplay from '../../../shared/model-details-display';
import SimpleDrawer from '../../../shared/simple-drawer';
import ReportsDeleteDialog from './reports-delete-dialog';
import ReportsRunDialog from './reports-run-dialog';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Check from '@mui/icons-material/Check';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreVert from '@mui/icons-material/MoreVert';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Update from '@mui/icons-material/Update';

// Display component

interface DrawerContentProps {
  onClose: () => void;
  selectedReport: ReportModel;
  reportTypes: ReportType[] | undefined;
  selectedSharedDrives?: SharedDriveModel[];
  onEdit?: (report: ReportModel) => void;
  onRun?: (report: ReportModel) => void;
  onDelete?: (report: ReportModel) => void;
  canEdit?: boolean;
  canRun?: boolean;
  canDelete?: boolean;
  runDisabledReason?: string;
  editDisabledReason?: string;
  deleteDisabledReason?: string;
  isRunning?: boolean;
}

function DrawerContent({
  onClose,
  selectedReport,
  reportTypes,
  selectedSharedDrives,
  onEdit,
  onRun,
  onDelete,
  canEdit,
  canRun,
  canDelete,
  runDisabledReason,
  editDisabledReason,
  deleteDisabledReason,
  isRunning,
}: Readonly<DrawerContentProps>) {
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const MAX_CHIPS = 8;

  const reportTypeFind = reportTypes?.find(
    (reportType) => reportType.name === selectedReport.reportType
  );

  const basicInfoData: Record<string, React.ReactNode> = useMemo(() => {
    const emailChips = selectedReport.emailsToNotify?.length ? (
      <Stack direction="row" gap={1} flexWrap="wrap">
        {selectedReport.emailsToNotify.map((email) => (
          <AvatarChip key={email} email={email} type="User" />
        ))}
      </Stack>
    ) : null;

    const shareChips = selectedReport.permissions?.length ? (
      <Stack direction="row" gap={1} flexWrap="wrap">
        {selectedReport.permissions.map((p) => (
          <AvatarChip
            key={p.userEmail}
            email={p.userEmail || 'N/A'}
            type="User"
          />
        ))}
      </Stack>
    ) : null;

    const columnChips = selectedReport.columns?.length ? (
      <Stack gap={1}>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {selectedReport.columns
            .slice(
              0,
              showAllColumns ? selectedReport.columns.length : MAX_CHIPS
            )
            .map(
              (col: import('../../../../types').SpreadSheetColumn | string) => {
                const colName =
                  typeof col === 'string' ? col : col.columnName || col.name;
                return (
                  <AvatarChip key={colName} email={colName} type="Unknown" />
                );
              }
            )}
        </Stack>
        {selectedReport.columns.length > MAX_CHIPS && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-start',
            }}
          >
            <Button
              size="small"
              onClick={() => setShowAllColumns(!showAllColumns)}
            >
              {showAllColumns
                ? 'Show less'
                : `+${selectedReport.columns.length - MAX_CHIPS} more`}
            </Button>
          </Box>
        )}
      </Stack>
    ) : null;

    const data: Record<string, React.ReactNode> = {
      'Created By': selectedReport.createdBy ? (
        <AvatarChip email={selectedReport.createdBy} type="User" />
      ) : (
        'N/A'
      ),
      'Report Type':
        reportTypeFind?.niceName || selectedReport.reportType || 'N/A',
      'Generated File Name': selectedReport.generatedFileName || 'N/A',
    };

    if (selectedReport.manualRun) data['Manual Run'] = <Check />;
    if (selectedReport.scheduled) data['Scheduled Run'] = <Check />;
    if (selectedReport.destFolderName)
      data['Destination Folder'] = selectedReport.destFolderName;

    if (reportTypeFind?.hasDateRange) {
      const reportWithDateRange = selectedReport as ReportModel & {
        dateRange?: [number, number];
      };
      data['Date Range'] = reportWithDateRange?.dateRange
        ? `${dayjs(reportWithDateRange.dateRange[0]).format(
            'MM/DD/YYYY'
          )} - ${dayjs(reportWithDateRange.dateRange[1]).format('MM/DD/YYYY')}`
        : selectedReport.startDate && selectedReport.endDate
        ? `${dayjs(selectedReport.startDate).format('MM/DD/YYYY')} - ${dayjs(
            selectedReport.endDate
          ).format('MM/DD/YYYY')}`
        : '-';
    }

    if (reportTypeFind?.hasDateSelection) {
      data['Date Selection'] = selectedReport.startDate
        ? dayjs(selectedReport.startDate).format('MM/DD/YYYY')
        : dayjs().subtract(3, 'day').format('MM/DD/YYYY');
    }

    if (columnChips) data['Columns'] = columnChips;
    if (emailChips) data['Email Report To'] = emailChips;
    if (shareChips) data['Share Access To'] = shareChips;

    return data;
  }, [selectedReport, reportTypeFind, showAllColumns]);

  const sourcesData: Record<string, React.ReactNode> | null = useMemo(() => {
    const sourceType = selectedReport.sourceType;
    const allSource = selectedReport.allSource;
    const sources = selectedReport.sources || [];

    if (!sourceType && !sources.length && !allSource) return null;

    const formatSourceType = (type: string | null): string => {
      switch (type) {
        case 'Resource_Calendar':
          return 'Resource Calendar';
        case 'Shared_Drive':
          return 'Shared Drive';
        case null:
          return 'N/A';
        default:
          return type;
      }
    };

    const data: Record<string, React.ReactNode> = {};

    if (
      sourceType &&
      [
        'User',
        'Group',
        'OrgUnit',
        'Domain',
        'Resource_Calendar',
        'Shared_Drive',
      ].includes(sourceType)
    ) {
      data['Source Type'] = formatSourceType(sourceType);
    }

    if (allSource || sources.length > 0) {
      if (allSource) {
        data['Sources'] = 'All';
      } else {
        const typeMap = {
          User: 'User',
          Group: 'Group',
          gPanel_Role: 'gPanel Role',
          Resource_Calendar: 'Resource Calendar',
          OrgUnit: 'OrgUnit',
          Shared_Drive: 'Shared Drive',
          Domain: 'Domain',
        };

        const chipType = (typeMap[sourceType as keyof typeof typeMap] ||
          'Unknown') as
          | 'User'
          | 'Group'
          | 'Domain'
          | 'gPanel Role'
          | 'OrgUnit'
          | 'Shared Drive'
          | 'RegEx'
          | 'User Drive'
          | 'Resource Calendar'
          | 'Unknown';

        data['Sources'] = (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {sources.map((s) => {
              let label = s;
              if (sourceType === 'Shared_Drive' && selectedSharedDrives) {
                label =
                  selectedSharedDrives.find((drive) => drive.id === s)?.name ??
                  s;
              }
              return <AvatarChip key={s} email={label} type={chipType} />;
            })}
          </Stack>
        );
      }
    }

    return Object.keys(data).length > 0 ? data : null;
  }, [selectedReport, selectedSharedDrives]);

  const scheduleData: Record<string, React.ReactNode> | null = useMemo(() => {
    if (!selectedReport.scheduled) return null;

    const times = selectedReport.times || [];
    const dates = selectedReport.dates || [];

    const data: Record<string, React.ReactNode> = {
      Times: times
        .map((time) => {
          const t = Number.parseInt(time);
          if (t === 0) return '12 AM';
          if (t < 12) return `${t} AM`;
          if (t === 12) return '12 PM';
          return `${t - 12} PM`;
        })
        .join(', '),
      Dates: dates.join(', '),
    };

    if (
      selectedReport.scheduleEndDate &&
      selectedReport.scheduleEndDate !== 0
    ) {
      data['Expiration Date'] = dayjs(selectedReport.scheduleEndDate).format(
        'MM/DD/YYYY'
      );
    }

    return data;
  }, [selectedReport]);

  return (
    <SimpleDrawer title="Report Details" onClose={onClose}>
      <Stack pl={2} pr={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {selectedReport.reportName || 'N/A'}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Actions">
              <IconButton onClick={handleClick} id="report-details-menu-button">
                <MoreVert />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              elevation={1}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'report-details-menu-button',
              }}
            >
              <DisabledTooltipMenuItem
                disabled={
                  (!canRun ||
                    selectedReport.manualRun === false ||
                    isRunning) &&
                  selectedReport.status !== 'RUNNING'
                }
                data-disabled-reason={runDisabledReason}
                onClick={() => {
                  if (selectedReport.status !== 'RUNNING') {
                    onRun?.(selectedReport);
                  }
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <PlayArrow fontSize="small" />
                </ListItemIcon>
                Run
              </DisabledTooltipMenuItem>

              <DisabledTooltipMenuItem
                disabled={!canEdit}
                data-disabled-reason={editDisabledReason}
                onClick={() => {
                  onEdit?.(selectedReport);
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                Edit
              </DisabledTooltipMenuItem>

              <DisabledTooltipMenuItem
                disabled={!canDelete}
                data-disabled-reason={deleteDisabledReason}
                onClick={() => {
                  onDelete?.(selectedReport);
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <Delete fontSize="small" />
                </ListItemIcon>
                Delete
              </DisabledTooltipMenuItem>
            </Menu>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedReport.description || 'No description provided.'}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          sx={{ mb: 2, alignItems: 'center' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <Update fontSize="small" />
            <Typography variant="body2" color="inherit">
              Last Run Date:{' '}
              {selectedReport.lastRunDate
                ? dayjs(selectedReport.lastRunDate).format(
                    'MMM D, YYYY, h:mm A'
                  )
                : 'Never Ran'}
            </Typography>
          </Stack>
          <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 14 }}>
            •
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <Check fontSize="small" />
            <Typography variant="body2" color="inherit">
              Created Date:{' '}
              {selectedReport.createdDate
                ? dayjs(selectedReport.createdDate).format(
                    'MMM D, YYYY, h:mm A'
                  )
                : 'N/A'}
            </Typography>
          </Stack>
        </Stack>

        <ModelDetailsDisplay title="Basic Information" data={basicInfoData} />
        {sourcesData && (
          <ModelDetailsDisplay title="Sources" data={sourcesData} />
        )}
        {scheduleData && (
          <ModelDetailsDisplay title="Schedule" data={scheduleData} />
        )}
      </Stack>
    </SimpleDrawer>
  );
}

/**
 * Global, self-contained report detail drawer.
 * Driven entirely by the `?selectedReport=<id>` URL search param.
 * Place this once at the App level — no props required.
 */
export default function ReportDrawerDetailsOld() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toolsRoles, changeLogRoles, usersTier } = useUserSettings();

  const selectedReportId = searchParams.get('selectedReport');

  // data
  const { data: reports } = useQuery({
    queryKey: [UserKeys.Reports],
    queryFn: getAllReports,
    enabled: !!selectedReportId,
  });

  const { data: reportTypes, isLoading: reportTypesLoading } = useQuery({
    queryKey: [UserKeys.ReportTypes],
    queryFn: getReportTypes,
    enabled: !!selectedReportId,
  });

  const removedHiddenReports =
    reportTypes?.filter((rt) => rt.category !== 'hidden') ?? [];
  const selectedReport =
    reports?.find((r) => r.id.toString() === selectedReportId) ?? null;
  const selectedReportType = removedHiddenReports.find(
    (t) => t.name === selectedReport?.reportType
  );

  // Fetch shared drives only for the currently viewed report.
  const sharedDriveSources =
    selectedReport?.sourceType === 'Shared_Drive'
      ? selectedReport.sources ?? []
      : [];
  const { data: selectedSharedDrives } = useQuery({
    ...getSharedDrivesByIdsQuery(sharedDriveSources),
    enabled: sharedDriveSources.length > 0,
  });

  // close drawer
  const handleClose = () => {
    setSearchParams((prev) => {
      prev.delete('selectedReport');
      return prev;
    });
  };

  const {
    handleStartReport,
    handleConfirmRunDialog,
    handleConfirmRun,
    isRunning,
    reportForRun,
    openRunConfirmDialog,
    closeRunConfirmDialog,
    changeLogMessage,
    setChangeLogMessage,
    openChangeLogDialog,
    closeChangeLogDialog,
    handleEditReport,
    openDeleteDialog,
    setOpenDeleteDialog,
  } = useReportActions({
    toolsRoles,
    changeLogRoles,
    removedHiddenReports,
    onRunSettled: handleClose,
  });

  const [deleteTargetRows, setDeleteTargetRows] = useState<number[]>([]);

  // permissions
  function getToolTipText(rt: typeof selectedReportType) {
    if (!rt) return '';
    if (usersTier.rank <= gPanelTiers.starter.rank && !rt.includeInStarter) {
      return Messages.Errors.Tier.Acting(gPanelTiers.standard.displayValue);
    }
    if (
      !isAbleToSeeUIElement(toolsRoles, ToolsRoles.MaunalRunReport.value) &&
      !isAbleToSeeUIElement(toolsRoles, rt.role)
    ) {
      const matchingRole = Object.keys(ToolsRoles).find(
        (k) => ToolsRoles[k].value === rt.role
      );
      return matchingRole
        ? Messages.Errors.Permission.Acting(ToolsRoles[matchingRole].name)
        : Messages.Errors.Permission.Acting(rt.role);
    }
    return rt.description;
  }

  const canEdit = isAbleToSeeUIElement(toolsRoles, ToolsRoles.EditReport.value);
  const runTooltipText = getToolTipText(selectedReportType);
  const canRun =
    !reportTypesLoading && runTooltipText === selectedReportType?.description;
  const canDelete = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.DeleteReport.value
  );

  const runPermissionBlockedReason =
    reportTypesLoading || canRun ? undefined : runTooltipText || undefined;
  const runDisabledReason = isRunning
    ? 'Report is running'
    : selectedReport?.manualRun === false
    ? 'This report is scheduled'
    : runPermissionBlockedReason;
  const editDisabledReason = canEdit
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.EditReport.name);
  const deleteDisabledReason = canDelete
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.DeleteReport.name);

  // render
  return (
    <>
      <Drawer
        anchor="right"
        open={!!selectedReport}
        onClose={handleClose}
        sx={{ '& .MuiDrawer-paper': { width: { xs: '100vw', md: '45%' } } }}
      >
        {selectedReport && (
          <DrawerContent
            onClose={handleClose}
            selectedReport={selectedReport}
            reportTypes={removedHiddenReports}
            selectedSharedDrives={selectedSharedDrives}
            canEdit={canEdit}
            canRun={canRun}
            canDelete={canDelete}
            isRunning={isRunning}
            onEdit={(r) => {
              handleClose();
              handleEditReport(r.id);
            }}
            onRun={handleStartReport}
            onDelete={(r) => {
              setDeleteTargetRows([r.id]);
              setOpenDeleteDialog(true);
            }}
            runDisabledReason={runDisabledReason}
            editDisabledReason={editDisabledReason}
            deleteDisabledReason={deleteDisabledReason}
          />
        )}
      </Drawer>

      {openDeleteDialog && deleteTargetRows.length > 0 && (
        <ReportsDeleteDialog
          open={openDeleteDialog}
          handleClose={() => setOpenDeleteDialog(false)}
          selectedRows={deleteTargetRows}
          setSelectedRows={setDeleteTargetRows}
          changeLogRoles={changeLogRoles}
        />
      )}

      {openRunConfirmDialog && reportForRun && (
        <ReportsRunDialog
          open={openRunConfirmDialog}
          onClose={closeRunConfirmDialog}
          reportName={reportForRun.reportName}
          isRunning={isRunning}
          onConfirm={handleConfirmRunDialog}
        />
      )}

      {openChangeLogDialog && reportForRun && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={closeChangeLogDialog}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={handleConfirmRun}
          mutateLoading={isRunning}
        />
      )}
    </>
  );
}
